import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { musicApi, type UnmatchedFile } from "@/api/music";
import { Button } from "@/components/ui/Button";
import { Checkbox, Field, Select, TextInput } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { DownloadsEnabledCard } from "@/components/DownloadsEnabledCard";
import { PageHeader } from "@/components/ui/PageHeader";
import { Status } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";

/**
 * Preset intervals presented as a dropdown. Custom values entered via
 * the API show as "Every N hours" but the UI nudges users toward
 * these — covers the realistic cases without a free-form input.
 */
const INTERVAL_PRESETS: { value: number; label: string }[] = [
  { value: 0, label: "Manual only" },
  { value: 1, label: "Every hour" },
  { value: 6, label: "Every 6 hours" },
  { value: 12, label: "Every 12 hours" },
  { value: 24, label: "Daily" },
  { value: 168, label: "Weekly" },
];

/** Issue #10: presets for the RSS-sync cadence. Minutes-keyed because
 *  the loop's resolution is minutes, not hours. */
const RSS_INTERVAL_PRESETS: { value: number; label: string }[] = [
  { value: 0, label: "Off (manual only)" },
  { value: 15, label: "Every 15 minutes" },
  { value: 30, label: "Every 30 minutes" },
  { value: 60, label: "Every hour" },
  { value: 180, label: "Every 3 hours" },
  { value: 360, label: "Every 6 hours" },
];

export function MusicSettings() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [libraryPath, setLibraryPath] = useState("");
  const [intervalHours, setIntervalHours] = useState(0);
  const [fetchLyrics, setFetchLyrics] = useState(false);
  // Issue #17: opt-in tag write-back. Default off — flipping this on
  // lets the importer rewrite user files, so the user must explicitly
  // opt in.
  const [writeTagsOnImport, setWriteTagsOnImport] = useState(false);
  const [rssIntervalMinutes, setRssIntervalMinutes] = useState(30);
  // Issue #21: path-templating settings. The defaults reproduce the
  // legacy hard-coded layout — leaving these untouched is a no-op
  // for an existing library.
  const [tplArtistDir, setTplArtistDir] = useState("{Artist}");
  const [tplAlbumDir, setTplAlbumDir] = useState("{Album}");
  const [tplFilename, setTplFilename] = useState(
    "{Track:02} - {Title}.{ext}",
  );
  // Plex Media Server credentials for the Spotify-import → Plex
  // playlist materialiser. URL is plain text; token is masked since
  // the backend never echoes it back (only returns a boolean
  // "is it set" flag).
  const [plexBaseUrl, setPlexBaseUrl] = useState("");
  const [plexToken, setPlexToken] = useState("");

  const { data: settings, isLoading } = useQuery({
    queryKey: ["settings", "music"],
    queryFn: musicApi.settings.get,
  });
  // Status polls while a scan is running so the user sees it tick over.
  const { data: status } = useQuery({
    queryKey: ["music", "scan", "status"],
    queryFn: musicApi.scan.status,
    refetchInterval: (q) => (q.state.data?.running ? 3000 : 30000),
  });

  // Sync local form state from the server whenever the settings load.
  useEffect(() => {
    if (settings) {
      setLibraryPath(settings.library_path ?? "");
      setIntervalHours(settings.scan_interval_hours);
      setFetchLyrics(settings.fetch_lyrics);
      setWriteTagsOnImport(settings.write_tags_on_import);
      setRssIntervalMinutes(settings.rss_interval_minutes);
      setTplArtistDir(settings.path_template_artist_dir);
      setTplAlbumDir(settings.path_template_album_dir);
      setTplFilename(settings.path_template_filename);
      setPlexBaseUrl(settings.plex_base_url ?? "");
      // Don't pre-fill the token from the server (we don't get the
      // value back, only the boolean). Leave empty so the user types
      // a new one or leaves it blank to no-op the field.
    }
  }, [
    settings?.library_path,
    settings?.scan_interval_hours,
    settings?.fetch_lyrics,
    settings?.write_tags_on_import,
    settings?.rss_interval_minutes,
    settings?.path_template_artist_dir,
    settings?.path_template_album_dir,
    settings?.path_template_filename,
    settings?.plex_base_url,
  ]);

  const saveMut = useMutation({
    mutationFn: (body: {
      library_path?: string;
      scan_interval_hours?: number;
      fetch_lyrics?: boolean;
      write_tags_on_import?: boolean;
      rss_interval_minutes?: number;
      path_template_artist_dir?: string;
      path_template_album_dir?: string;
      path_template_filename?: string;
      plex_base_url?: string;
      plex_token?: string;
    }) => musicApi.settings.update(body),
    onSuccess: () => {
      toast("Music settings saved", "success");
      qc.invalidateQueries({ queryKey: ["settings", "music"] });
      qc.invalidateQueries({ queryKey: ["music", "scan", "status"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Save failed", "error"),
  });

  // Issue #10: manual "Sync now" — runs one tick synchronously and
  // surfaces the per-tick summary. Useful right after the user
  // monitors a fresh artist with already-released albums.
  const rssRunMut = useMutation({
    mutationFn: () => musicApi.rssSync.runNow(),
    onSuccess: (s) => {
      const tone =
        s.failed > 0 ? "info" : s.dispatched > 0 ? "success" : "info";
      toast(
        s.considered === 0
          ? "RSS sync: nothing eligible right now"
          : `RSS sync: dispatched ${s.dispatched} of ${s.considered} (${s.skipped} no match, ${s.failed} failed)`,
        tone,
      );
      qc.invalidateQueries({ queryKey: ["grabs"] });
      qc.invalidateQueries({ queryKey: ["music"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "RSS sync failed", "error"),
  });

  const scanMut = useMutation({
    mutationFn: () => musicApi.scan.start(),
    onSuccess: () => {
      toast("Scan started — refresh in a moment to see progress", "info");
      qc.invalidateQueries({ queryKey: ["music", "scan", "status"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Scan failed to start", "error"),
  });

  const onSave = (e: FormEvent) => {
    e.preventDefault();
    saveMut.mutate({
      library_path: libraryPath.trim(),
      scan_interval_hours: intervalHours,
      fetch_lyrics: fetchLyrics,
      write_tags_on_import: writeTagsOnImport,
      rss_interval_minutes: rssIntervalMinutes,
      path_template_artist_dir: tplArtistDir.trim(),
      path_template_album_dir: tplAlbumDir.trim(),
      path_template_filename: tplFilename.trim(),
      plex_base_url: plexBaseUrl.trim(),
      // Only send a token when the user typed something — empty
      // leaves the existing one in place. Backend three-state
      // (omit / empty / value) handles the clear case via an
      // explicit "" if the user wants to revoke.
      plex_token: plexToken ? plexToken : undefined,
    });
  };

  // Manual lyrics backfill — POSTs to `/music/lyrics/backfill` and
  // surfaces a toast with the per-track tally so the user knows
  // how many sidecars actually got written.
  const backfillMut = useMutation({
    mutationFn: () => musicApi.lyrics.backfill(),
    onSuccess: (s) => {
      toast(
        s.considered === 0
          ? "No tracks needed lyrics — library is up to date"
          : `Lyrics backfill: wrote ${s.written} of ${s.considered} (${s.misses} miss${s.misses === 1 ? "" : "es"})`,
        s.misses === 0 ? "success" : "info",
      );
      qc.invalidateQueries({ queryKey: ["music"] });
    },
    onError: (e) =>
      toast(
        e instanceof Error ? e.message : "Lyrics backfill failed",
        "error",
      ),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Settings"
        title="Music"
        description="Library path + scan schedule. The scanner walks the path, reads tags, and links existing files to artist/album/track rows by MusicBrainz IDs."
      />

      <DownloadsEnabledCard kind="music" />

      <ScanStatusCard
        loading={!status}
        running={status?.running ?? false}
        lastScanAt={status?.last_scan_at ?? null}
        unmatchedCount={status?.unmatched_count ?? 0}
        onScanNow={() => scanMut.mutate()}
        scanning={scanMut.isPending}
      />

      <form onSubmit={onSave} className="space-y-4">
        <Field
          label="Library path"
          hint={
            settings?.library_path_source === "env"
              ? "Currently sourced from MUSIC_LIBRARY_PATH env var. Set a value here to override."
              : "Absolute path arr-in-one renames downloads into and scans for existing files. Must be readable + writable by the server."
          }
        >
          <TextInput
            value={libraryPath}
            placeholder="/music"
            onChange={(e) => setLibraryPath(e.target.value)}
          />
        </Field>

        <Field
          label="Scan interval"
          hint="How often the background scanner walks the library. Manual scans (above) work regardless."
        >
          <Select
            value={String(intervalHours)}
            onChange={(e) => setIntervalHours(Number(e.target.value))}
          >
            {INTERVAL_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
            {INTERVAL_PRESETS.every((p) => p.value !== intervalHours) && (
              <option value={intervalHours}>
                Every {intervalHours} hours (custom)
              </option>
            )}
          </Select>
        </Field>

        <div className="space-y-2 border-t border-line pt-4">
          {/* Issue #10: RSS / indexer sync cadence. Distinct from the
           * MusicBrainz refresh below (which discovers newly-announced
           * release-groups upstream) and the cutoff-unmet upgrade
           * poller (which targets has_file=1 albums). This loop fires
           * fresh searches against indexers for monitored albums whose
           * release date has passed but `has_file=0`. Capped at 25
           * grabs per tick to spread indexer load. */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <Field
                label="RSS sync interval"
                hint="How often arr-in-one re-asks indexers about monitored albums whose release date has passed but no file is on disk yet. Default 30 minutes; lower numbers grab the moment a release lands but burn more indexer queries."
              >
                <Select
                  value={String(rssIntervalMinutes)}
                  onChange={(e) =>
                    setRssIntervalMinutes(Number(e.target.value))
                  }
                >
                  {RSS_INTERVAL_PRESETS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                  {RSS_INTERVAL_PRESETS.every(
                    (p) => p.value !== rssIntervalMinutes,
                  ) && (
                    <option value={rssIntervalMinutes}>
                      Every {rssIntervalMinutes} minutes (custom)
                    </option>
                  )}
                </Select>
              </Field>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => rssRunMut.mutate()}
              loading={rssRunMut.isPending}
              title="Run one RSS-sync tick now, regardless of cadence."
            >
              Sync now
            </Button>
          </div>
        </div>

        <div className="space-y-2 border-t border-line pt-4">
          <div className="flex items-start justify-between gap-4">
            <label className="flex cursor-pointer items-start gap-3 text-sm">
              <Checkbox
                checked={fetchLyrics}
                onChange={(e) => setFetchLyrics(e.target.checked)}
              />
              <span>
                <span className="block font-medium">
                  Fetch lyrics on import
                </span>
                <span className="mt-0.5 block text-xs text-foreground/55">
                  Pulls `.lrc` sidecars from{" "}
                  <a
                    className="underline hover:text-accent"
                    href="https://lrclib.net"
                    target="_blank"
                    rel="noreferrer"
                  >
                    LRCLib
                  </a>{" "}
                  during album imports + a daily backfill sweep. Failures are
                  non-fatal and never block a successful import.
                </span>
              </span>
            </label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => backfillMut.mutate()}
              loading={backfillMut.isPending}
              disabled={!fetchLyrics && !settings?.fetch_lyrics}
              title={
                !fetchLyrics && !settings?.fetch_lyrics
                  ? "Enable lyrics fetching first, then save."
                  : "Sweep the library for tracks without lyrics."
              }
            >
              Backfill missing lyrics
            </Button>
          </div>
        </div>

        {/* Issue #17: tag write-back. Off by default — flipping this
         *  on lets the importer rewrite user audio files with the
         *  MusicBrainz canonical metadata arr-in-one resolved.
         *  Failures are non-fatal and never block a successful
         *  import. */}
        <div className="space-y-2 border-t border-line pt-4">
          <label className="flex cursor-pointer items-start gap-3 text-sm">
            <Checkbox
              checked={writeTagsOnImport}
              onChange={(e) => setWriteTagsOnImport(e.target.checked)}
            />
            <span>
              <span className="block font-medium">
                Write canonical tags on import
              </span>
              <span className="mt-0.5 block text-xs text-foreground/55">
                After a track is placed, rewrite its audio tags
                (artist, album, title, track #, year, MusicBrainz IDs)
                from MusicBrainz canonical metadata. Off by default —
                flipping this on lets arr-in-one rewrite your files.
                Failures are logged and never block a successful
                import.
              </span>
            </span>
          </label>
        </div>

        {/* Issue #21: per-segment path templates. Defaults reproduce
         * the legacy hard-coded layout. The layout previewed below the
         * three inputs makes it obvious what the importer will produce
         * before saving — no need to import a file to find out. */}
        <div className="space-y-3 border-t border-line pt-4">
          <div>
            <h3 className="text-sm font-medium">Path templates</h3>
            <p className="mt-0.5 text-xs leading-relaxed text-foreground/55">
              How the importer names the artist directory, album directory,
              and track filename under the library root. Defaults match the
              previous hard-coded layout — leave them as-is to keep your
              existing library shape.
            </p>
          </div>
          <Field
            label="Artist directory"
            hint="Path under the library root. Variables: {Artist}, {Year}."
          >
            <TextInput
              value={tplArtistDir}
              placeholder="{Artist}"
              onChange={(e) => setTplArtistDir(e.target.value)}
            />
          </Field>
          <Field
            label="Album directory"
            hint="Path under the artist directory. Variables: {Album}, {Year}, {Artist}."
          >
            <TextInput
              value={tplAlbumDir}
              placeholder="{Album}"
              onChange={(e) => setTplAlbumDir(e.target.value)}
            />
          </Field>
          <Field
            label="Track filename"
            hint="The file basename. Variables: {Track}, {Track:02} (zero-padded), {Title}, {Disc}, {Artist}, {Album}, {Year}, {ext}."
          >
            <TextInput
              value={tplFilename}
              placeholder="{Track:02} - {Title}.{ext}"
              onChange={(e) => setTplFilename(e.target.value)}
            />
          </Field>
          <div className="rounded border border-line bg-surface/40 px-3 py-2 font-mono text-[11px] text-foreground/65">
            <div className="text-[10px] uppercase tracking-[0.12em] text-foreground/45">
              Preview
            </div>
            <div className="mt-1 break-all">
              {`<library>/${tplArtistDir || "{Artist}"}/${tplAlbumDir || "{Album}"}/${tplFilename || "{Track:02} - {Title}.{ext}"}`}
            </div>
          </div>
        </div>

        {/* Plex integration — drives the Spotify-import → Plex
            playlist materialiser. Optional: leaving these blank
            just skips the playlist step (the Spotify-import flow
            still works for adding artists + queuing downloads). */}
        <fieldset className="space-y-4 rounded border border-line bg-surface/30 p-4">
          <legend className="px-1 font-mono text-[10px] uppercase tracking-[0.14em] text-foreground/55">
            Plex Media Server
          </legend>
          <Field
            label="Plex base URL"
            hint="e.g. http://localhost:32400 or https://plex.example.com. Used by the Spotify playlist importer to create matching Plex playlists. Leave blank to skip the playlist step."
          >
            <TextInput
              value={plexBaseUrl}
              placeholder="http://localhost:32400"
              onChange={(e) => setPlexBaseUrl(e.target.value)}
              type="url"
            />
          </Field>
          <Field
            label="X-Plex-Token"
            hint={
              settings?.plex_token_set
                ? "A token is currently stored. Leave blank to keep it, type a new one to replace, or clear and save to revoke."
                : "Plex auth token — find it via any item's 'Get Info → XML' link in the Plex web UI. Stored as plain text in app_settings."
            }
          >
            <TextInput
              value={plexToken}
              placeholder={
                settings?.plex_token_set ? "•••••••• (set)" : "X-Plex-Token=…"
              }
              onChange={(e) => setPlexToken(e.target.value)}
              type="password"
              autoComplete="off"
            />
          </Field>
        </fieldset>

        <div className="flex justify-end border-t border-line pt-3">
          <Button type="submit" loading={saveMut.isPending} disabled={isLoading}>
            Save
          </Button>
        </div>
      </form>

      <UnmatchedSection count={status?.unmatched_count ?? 0} />
    </div>
  );
}

function ScanStatusCard({
  loading,
  running,
  lastScanAt,
  unmatchedCount,
  onScanNow,
  scanning,
}: {
  loading: boolean;
  running: boolean;
  lastScanAt: string | null;
  unmatchedCount: number;
  onScanNow: () => void;
  scanning: boolean;
}) {
  return (
    <div className="rounded border border-line bg-surface/40 px-4 py-3 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="eyebrow">Scanner</div>
          <div className="mt-1">
            {loading ? (
              <span className="text-foreground/55">Loading…</span>
            ) : running ? (
              <Status tone="info" pulse>Scan running</Status>
            ) : lastScanAt ? (
              <span>
                Last scan{" "}
                <span className="text-foreground/70">
                  {new Date(lastScanAt).toLocaleString()}
                </span>
              </span>
            ) : (
              <span className="text-foreground/60">
                Never scanned. Configure the library path below and run a scan.
              </span>
            )}
          </div>
          {unmatchedCount > 0 && (
            <div className="mt-1 text-xs text-foreground/50">
              {unmatchedCount} unmatched file{unmatchedCount === 1 ? "" : "s"}{" "}
              from the last scan — see below.
            </div>
          )}
        </div>
        <Button
          type="button"
          onClick={onScanNow}
          loading={scanning || running}
          disabled={running}
        >
          Scan now
        </Button>
      </div>
    </div>
  );
}

function UnmatchedSection({ count }: { count: number }) {
  const [expanded, setExpanded] = useState(false);
  // Single-file claim path stays for backwards-compat (one row, one
  // modal). Batch claim is opt-in via the per-row checkboxes.
  const [claiming, setClaiming] = useState<UnmatchedFile | null>(null);
  const [batching, setBatching] = useState<UnmatchedFile[] | null>(null);
  // Selection is a Set of unmatched-row ids. Cleared when the
  // underlying list refetches (e.g. after a successful batch claim).
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ["music", "scan", "unmatched"],
    queryFn: () => musicApi.scan.unmatched(200),
    enabled: expanded && count > 0,
  });

  // Drop selections that no longer exist in the loaded list (e.g. the
  // user just batch-claimed 5 rows and the refetch removed them). Run
  // on every data change.
  useEffect(() => {
    if (!data) return;
    const loadedIds = new Set(data.map((u) => u.id));
    setSelected((prev) => {
      let changed = false;
      const next = new Set<number>();
      for (const id of prev) {
        if (loadedIds.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [data]);

  if (count === 0) return null;

  const allChecked =
    !!data && data.length > 0 && data.every((u) => selected.has(u.id));
  const someChecked = selected.size > 0 && !allChecked;

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () => {
    if (!data) return;
    if (allChecked) {
      setSelected(new Set());
    } else {
      setSelected(new Set(data.map((u) => u.id)));
    }
  };

  const startBatch = () => {
    if (!data || selected.size === 0) return;
    const files = data.filter((u) => selected.has(u.id));
    setBatching(files);
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="eyebrow">Unmatched files</h2>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <span className="font-mono text-[11px] text-foreground/55">
              {selected.size} selected
            </span>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "Hide" : "Show"}
          </Button>
        </div>
      </div>
      <p className="text-xs leading-relaxed text-foreground/55">
        Files the scanner couldn't link to a known album. Tick rows to batch
        them, or use per-row Identify for one-offs. Track numbers come from
        each file's tags during a batch claim.
      </p>

      {expanded &&
        (isLoading ? (
          <p className="text-xs text-foreground/55">Loading…</p>
        ) : !data || data.length === 0 ? (
          <p className="text-xs text-foreground/55">None.</p>
        ) : (
          <>
            {selected.size > 0 && (
              <div className="sticky top-0 z-10 -mx-2 flex items-center justify-between gap-3 rounded-md border border-accent/30 bg-accent/10 px-3 py-2 text-xs backdrop-blur-md">
                <span className="text-foreground/85">
                  <span className="font-mono tabular-nums">
                    {selected.size}
                  </span>{" "}
                  file{selected.size === 1 ? "" : "s"} selected
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelected(new Set())}
                  >
                    Clear
                  </Button>
                  <Button type="button" size="sm" onClick={startBatch}>
                    Identify {selected.size}
                  </Button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto rounded-md border border-line bg-surface/30">
              <table className="w-full text-xs">
                <thead className="bg-surface-2/50">
                  <tr className="text-left">
                    <th className="w-10 px-3 py-2">
                      <Checkbox
                        checked={allChecked}
                        ref={(el) => {
                          if (el) el.indeterminate = someChecked;
                        }}
                        onChange={toggleAll}
                        aria-label="Select all unmatched files"
                      />
                    </th>
                    <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                      Path
                    </th>
                    <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                      Reason
                    </th>
                    <th className="w-24 px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line/60">
                  {data.map((u) => {
                    const checked = selected.has(u.id);
                    return (
                      <tr
                        key={u.id}
                        className={cn(
                          "transition hover:bg-surface-2/40",
                          checked && "bg-accent/5",
                        )}
                      >
                        <td className="px-3 py-2 align-top">
                          <Checkbox
                            checked={checked}
                            onChange={() => toggle(u.id)}
                            aria-label={`Select ${u.path}`}
                          />
                        </td>
                        <td
                          className="max-w-md truncate px-3 py-2 font-mono"
                          title={u.path}
                        >
                          {u.path}
                        </td>
                        <td className="px-3 py-2 text-foreground/65">
                          {u.reason}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => setClaiming(u)}
                          >
                            Identify
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ))}

      {claiming && (
        <ClaimModal file={claiming} onClose={() => setClaiming(null)} />
      )}
      {batching && (
        <BatchClaimModal
          files={batching}
          onClose={() => setBatching(null)}
          onComplete={() => {
            setBatching(null);
            setSelected(new Set());
          }}
        />
      )}
    </section>
  );
}

/**
 * Modal that walks the user through assigning an unmatched file to a
 * specific album + track. Two-step picker (artist → album) so the
 * album dropdown doesn't drown in thousands of options.
 */
function ClaimModal({
  file,
  onClose,
}: {
  file: UnmatchedFile;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [artistId, setArtistId] = useState<number | "">("");
  const [albumId, setAlbumId] = useState<number | "">("");
  const [trackNumber, setTrackNumber] = useState<string>("");

  const artists = useQuery({
    queryKey: ["music", "artists"],
    queryFn: () => musicApi.listArtists(),
  });
  const artistDetail = useQuery({
    queryKey: ["music", "artist", artistId],
    queryFn: () =>
      musicApi.getArtist(typeof artistId === "number" ? artistId : 0),
    enabled: typeof artistId === "number",
  });

  const sortedArtists = useMemo(
    () =>
      (artists.data ?? [])
        .slice()
        .sort((a, b) =>
          (a.sort_name || a.name).localeCompare(b.sort_name || b.name),
        ),
    [artists.data],
  );

  const claimMut = useMutation({
    mutationFn: () => {
      if (typeof albumId !== "number") {
        return Promise.reject(new Error("pick an album"));
      }
      const tn = trackNumber.trim();
      const body: { album_id: number; track_number?: number } = {
        album_id: albumId,
      };
      if (tn) {
        const n = Number(tn);
        if (Number.isFinite(n) && n >= 0) body.track_number = n;
      }
      return musicApi.scan.claim(file.id, body);
    },
    onSuccess: () => {
      toast("File claimed", "success");
      qc.invalidateQueries({ queryKey: ["music", "scan"] });
      qc.invalidateQueries({ queryKey: ["music", "stats"] });
      onClose();
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Claim failed", "error"),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    claimMut.mutate();
  };

  return (
    <Modal open onClose={onClose} title="Identify file" width="lg">
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="rounded border border-line bg-surface/40 px-3 py-2 text-xs">
          <div className="text-foreground/50">File</div>
          <div className="mt-0.5 truncate font-mono" title={file.path}>
            {file.path}
          </div>
        </div>

        <Field label="Artist">
          <Select
            value={artistId === "" ? "" : String(artistId)}
            onChange={(e) => {
              const v = e.target.value;
              setArtistId(v === "" ? "" : Number(v));
              setAlbumId("");
            }}
            disabled={artists.isLoading}
          >
            <option value="">— pick an artist —</option>
            {sortedArtists.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Album">
          <Select
            value={albumId === "" ? "" : String(albumId)}
            onChange={(e) => {
              const v = e.target.value;
              setAlbumId(v === "" ? "" : Number(v));
            }}
            disabled={typeof artistId !== "number" || artistDetail.isLoading}
          >
            <option value="">
              {typeof artistId !== "number"
                ? "— pick an artist first —"
                : "— pick an album —"}
            </option>
            {(artistDetail.data?.albums ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.title}
                {a.release_date ? ` (${a.release_date.slice(0, 4)})` : ""}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Track number"
          hint="Leave blank to use the file's tag-based track number (or 0 if missing)."
        >
          <TextInput
            inputMode="numeric"
            placeholder="auto from tags"
            value={trackNumber}
            onChange={(e) => setTrackNumber(e.target.value)}
          />
        </Field>

        <div className="flex justify-end gap-2 border-t border-line pt-3">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={claimMut.isPending}
            disabled={typeof albumId !== "number"}
          >
            Claim
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Batch claim modal — assigns N selected unmatched files to the same
// (artist, album). Track numbers come from each file's own tags
// (the backend reads ID3/Vorbis/MP4 metadata at claim time when the
// `track_number` field is omitted).
//
// Concurrency is bounded so we don't fire 50 simultaneous requests
// at the SQLite write path. Three at a time is a comfortable balance
// for batches up to ~50 files.
// ---------------------------------------------------------------------------

interface BatchProgress {
  done: number;
  succeeded: number;
  failed: number;
  /** Per-file failure messages keyed by unmatched id. */
  errors: Record<number, string>;
}

function BatchClaimModal({
  files,
  onClose,
  onComplete,
}: {
  files: UnmatchedFile[];
  onClose: () => void;
  onComplete: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [artistId, setArtistId] = useState<number | "">("");
  const [albumId, setAlbumId] = useState<number | "">("");
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<BatchProgress>({
    done: 0,
    succeeded: 0,
    failed: 0,
    errors: {},
  });

  const artists = useQuery({
    queryKey: ["music", "artists"],
    queryFn: () => musicApi.listArtists(),
  });
  const artistDetail = useQuery({
    queryKey: ["music", "artist", artistId],
    queryFn: () =>
      musicApi.getArtist(typeof artistId === "number" ? artistId : 0),
    enabled: typeof artistId === "number",
  });

  const sortedArtists = useMemo(
    () =>
      (artists.data ?? [])
        .slice()
        .sort((a, b) =>
          (a.sort_name || a.name).localeCompare(b.sort_name || b.name),
        ),
    [artists.data],
  );

  const runBatch = async (e: FormEvent) => {
    e.preventDefault();
    if (typeof albumId !== "number") {
      toast("Pick an album first", "error");
      return;
    }
    setRunning(true);
    setProgress({ done: 0, succeeded: 0, failed: 0, errors: {} });

    // Bounded concurrency. Each claim is a single SQLite UPDATE +
    // optional file move (the backend does the move) — small but not
    // free. Three workers is gentle on the server while still
    // parallelising the wall time.
    const CONCURRENCY = 3;
    const queue = files.slice();
    const localProgress: BatchProgress = {
      done: 0,
      succeeded: 0,
      failed: 0,
      errors: {},
    };
    const flush = () => setProgress({ ...localProgress });

    const workers = Array.from({ length: CONCURRENCY }, async () => {
      while (queue.length > 0) {
        const f = queue.shift();
        if (!f) return;
        try {
          await musicApi.scan.claim(f.id, { album_id: albumId as number });
          localProgress.succeeded += 1;
        } catch (err) {
          localProgress.failed += 1;
          localProgress.errors[f.id] =
            err instanceof Error ? err.message : "claim failed";
        } finally {
          localProgress.done += 1;
          flush();
        }
      }
    });
    await Promise.all(workers);

    setRunning(false);
    qc.invalidateQueries({ queryKey: ["music", "scan"] });
    qc.invalidateQueries({ queryKey: ["music", "stats"] });
    toast(
      `Identified ${localProgress.succeeded} of ${files.length} file${files.length === 1 ? "" : "s"}` +
        (localProgress.failed > 0 ? ` (${localProgress.failed} failed)` : ""),
      localProgress.failed === 0 ? "success" : "info",
    );

    // Auto-close on full success; leave open on partial failure so the
    // user can see which files didn't go through.
    if (localProgress.failed === 0) {
      onComplete();
    }
  };

  const failedRows = files.filter((f) => progress.errors[f.id]);

  return (
    <Modal
      open
      onClose={running ? () => {} : onClose}
      eyebrow={`Identify ${files.length} files`}
      title="Batch claim"
      width="lg"
    >
      <form onSubmit={runBatch} className="space-y-4">
        <div className="rounded-md border border-line bg-surface/40 px-3 py-2 text-xs">
          <div className="text-foreground/45">Files</div>
          <ul className="mt-1 max-h-32 space-y-0.5 overflow-y-auto font-mono text-[11px] text-foreground/80">
            {files.slice(0, 20).map((f) => (
              <li key={f.id} className="truncate" title={f.path}>
                {f.path}
              </li>
            ))}
            {files.length > 20 && (
              <li className="italic text-foreground/55">
                …and {files.length - 20} more
              </li>
            )}
          </ul>
        </div>

        <Field label="Artist">
          <Select
            value={artistId === "" ? "" : String(artistId)}
            onChange={(e) => {
              const v = e.target.value;
              setArtistId(v === "" ? "" : Number(v));
              setAlbumId("");
            }}
            disabled={artists.isLoading || running}
          >
            <option value="">— pick an artist —</option>
            {sortedArtists.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Album"
          hint="All selected files will be claimed against this album. Track numbers come from each file's own tags."
        >
          <Select
            value={albumId === "" ? "" : String(albumId)}
            onChange={(e) => {
              const v = e.target.value;
              setAlbumId(v === "" ? "" : Number(v));
            }}
            disabled={
              typeof artistId !== "number" || artistDetail.isLoading || running
            }
          >
            <option value="">
              {typeof artistId !== "number"
                ? "— pick an artist first —"
                : "— pick an album —"}
            </option>
            {(artistDetail.data?.albums ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.title}
                {a.release_date ? ` (${a.release_date.slice(0, 4)})` : ""}
              </option>
            ))}
          </Select>
        </Field>

        {(running || progress.done > 0) && (
          <div className="space-y-2">
            <div className="flex items-center justify-between font-mono text-[11px] text-foreground/65">
              <span>
                {progress.done} / {files.length} processed
              </span>
              <span>
                {progress.succeeded > 0 && (
                  <Status tone="good" variant="text">
                    {progress.succeeded} ok
                  </Status>
                )}
                {progress.failed > 0 && (
                  <>
                    {progress.succeeded > 0 && (
                      <span className="mx-2 text-foreground/35">·</span>
                    )}
                    <Status tone="bad" variant="text">
                      {progress.failed} failed
                    </Status>
                  </>
                )}
              </span>
            </div>
            <div className="h-1 overflow-hidden rounded bg-surface-2">
              <div
                className="h-full bg-accent transition-all"
                style={{
                  width: `${files.length === 0 ? 0 : (100 * progress.done) / files.length}%`,
                }}
              />
            </div>
            {failedRows.length > 0 && !running && (
              <details className="text-xs">
                <summary className="cursor-pointer text-foreground/55 hover:text-foreground">
                  Show {failedRows.length} failure
                  {failedRows.length === 1 ? "" : "s"}
                </summary>
                <ul className="mt-2 space-y-1 rounded-md border border-bad/30 bg-bad/5 px-3 py-2 font-mono text-[11px] text-foreground/75">
                  {failedRows.slice(0, 10).map((f) => (
                    <li key={f.id} className="truncate" title={f.path}>
                      <span className="text-foreground/55">{f.path}</span>
                      <span className="ml-2 text-bad">
                        — {progress.errors[f.id]}
                      </span>
                    </li>
                  ))}
                  {failedRows.length > 10 && (
                    <li className="italic text-foreground/55">
                      …and {failedRows.length - 10} more
                    </li>
                  )}
                </ul>
              </details>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-line pt-3">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={running}
          >
            {progress.done > 0 && !running ? "Done" : "Cancel"}
          </Button>
          <Button
            type="submit"
            loading={running}
            disabled={typeof albumId !== "number" || running}
          >
            {running
              ? `Identifying ${progress.done}/${files.length}…`
              : `Identify ${files.length} file${files.length === 1 ? "" : "s"}`}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
